import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { InputControlBase, ValueChangeEvent } from './../forms/input-control.base';
import { FormControl, FormGroup, FormBuilder, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { LocalDate } from '../../model/local-date';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { FormControlEx } from '../forms/form-control';
import { Subscription } from 'rxjs';
import { normalizeAstralTemporalInput } from '../astral-components/astral-temporal-input/astral-temporal-input.util';

@Component({
    selector: 'app-local-date-picker',
    templateUrl: './local-date-picker.component.html',
    styleUrls: ['./local-date-picker.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LocalDatePickerComponent extends InputControlBase implements OnInit {
  constructor(
    private _controlModelFactory: FormControlExFactory,
    private fb: FormBuilder
  ) {
    super();
  }
  @Input() showEmptyReadOnly: boolean;
  @Input() emptyIsNull: boolean;

  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean => {
      if (control?.invalid && (control.touched || control.dirty || form?.submitted)) {
        return true;
      }
      return !!this._getParentGroupError() && !!(this.control?.touched || this.control?.dirty || form?.submitted);
    },
  };

  private _dateControl: FormControlEx;

  get dateControl(): FormControlEx {
    if (!this._dateControl)
      this._dateControl = this._controlModelFactory.createWithOptions('DATE', this.date, {
        required: this.control.required,
        isReadonly: this.control.isReadonly,
        asyncValidator: this.control.asyncValidator,
        helpText: this.control.helpText as string,
        validator: this.control.validator,
      });
    return this._dateControl;
  }

  display: boolean = true;

  private _internalForm: FormGroup;
  get internalForm(): FormGroup {
    if (!this._internalForm) this._internalForm = this.fb.group({ date: this.dateControl });
    return this._internalForm;
  }

  get date() {
    if (this.control.value === null || this.control.value === undefined) return null;
    return LocalDate.fromDate(this.control.value).toDate();
  }

  set date(date) {
    let localDate = LocalDate.fromDate(date);
    this.dateControl.setValue(localDate.hasValue() ? localDate.toDate() : '');
  }

  private updating = false;
  onControlSet() {
    super.onControlSet();
    this.subscription.add(
      this.control.touchStateChanged.subscribe(val => {
        if (val) {
          this.dateControl.markAsTouched();
        } else {
          this.dateControl.markAsUntouched();
        }
      })
    );
    this.subscription.add(
      this.control.statusChanges.subscribe(val => {
        this.dateControl.updateValueAndValidity();
      })
    );
    this.subscription.add(
      this.control.validatorsChanged.subscribe(val => {
        this.dateControl.setValidators(val);
      })
    );
    this.subscription.add(
      this.control.valueChanges.subscribe(val => {
        if (!this.updating) {
          this.dateControl.setValue(this.date);
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.showEmptyReadOnly) this.display = true;
    else this.display = (this.control.value !== null && this.control.value !== undefined) || !this.control.isReadonly;
  }

  onKeyUp(a: any) {
    let value = a.currentTarget.value;
    if (typeof value === 'string' && /^\d{8}$/.test(value)) {
      const normalized = normalizeAstralTemporalInput(value, 'date');
      if (normalized) {
        const [year, month, day] = normalized.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        this.date = date;
        this.onDateChange(date);
      }
    }
  }

  onDateChange(date: Date) {
    this.updating = true;
    let value = this.control.value;
    let localDateValue = LocalDate.fromDate(date);
    if (!localDateValue.hasValue && this.emptyIsNull) localDateValue = null;
    this.control.setValue(localDateValue);
    this.control.markAsDirty();
    this.valueChange.emit({ formSnapshot: this.control.parent.value, newValue: this.control.value, previousValue: value });
    this.updating = false;
  }

  override onblur() {
    this.control?.markAsTouched();
    this.dateControl?.markAsTouched();
    super.onblur();
  }

  get errorMessage(): { message: string; params: any } {
    if (this.dateControl?.invalid && this.dateControl?.errors) {
      let key = Object.keys(this.dateControl.errors)[0];
      switch (key) {
        case 'matDatepickerParse':
          return { message: 'ERROR.DATE', params: {} };

        default:
          return this.dateControl.getErrorMessage();
      }
    }

    if (this.control?.invalid && this.control?.errors && (this.control.touched || this.control.dirty)) {
      return this.control.getErrorMessage();
    }

    const parentError = this._getParentGroupError();
    if (parentError && (this.control?.touched || this.control?.dirty)) {
      return parentError;
    }

    return null;
  }

  private _getParentGroupError(): { message: string; params: any } | null {
    const parentForm = this.control?.parent as FormGroup;
    if (!parentForm?.errors) return null;
    const controlName = Object.keys(parentForm.controls).find(key => parentForm.controls[key] === this.control);
    const error = controlName ? parentForm.errors[controlName] : null;
    return error?.message ? { message: error.message, params: error.params ?? {} } : null;
  }
}
